import { StandardProps } from "@/components/componentMap.ts";
import { useEffect, useRef, useState } from "react";
import { AddToBasketButton } from "./AddToBasketButton/AddToBasketButton";
import { useVariant } from "@/hooks/useVariant/useVariant.ts";
import { useScrollToButton } from "./hooks/useScrollToButton";
import { ExpressCheckoutButton } from "./ExpressCheckoutButton/ExpressCheckoutButton";
import { useButtonStates } from "./hooks/useButtonStates";
import { useBasket } from "./hooks/useBasket";
import { useQuantity } from "./hooks/useQuantity";
import { SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED } from "@/constants/constants.ts";
import { handleBasketSubmission } from "./utils/handleBasketSubmission";
import { APIError } from "./types";
import { ErrorMessage } from "./ErrorMessage/ErrorMessage";
import { ProductBasketItems } from "./ProductBasketItems/ProductBasketItems";
import { ensureIsActiveVariant } from "@/utils/product/ensureIsActive.ts";
import { useSharedServices } from "@/utils/services/useSharedServices.ts";
import { Recommendations } from "@/components/Recommendations/Recommendations";
import { Wishlist } from "@/components/AddToBasket/AddToBasketControls/Wishlist/Wishlist.tsx";
import classnames from "classnames/bind";
import { dispatchAnalyticsEvent } from "@/utils";
import { getDefaultCategory } from "@/utils/product/getDefaultCategory";
import { getOwnBrand } from "@/utils/product/getOwnBrand";
import { sanitiseTitle } from "@/components/Title/sanitiseTitle";

import styles from "./AddToBasketControls.module.scss";

const cx = classnames.bind(styles);

export const AddToBasketControls = (props: StandardProps) => {
  const { product, configProps, flags, runtimeConfig } = props;

  const a2bRef = useRef<HTMLDivElement>(null);
  const addToBasketButtonRef = useRef<HTMLButtonElement>(null);
  const { variant, isSelected } = useVariant(product);
  const activeVariant = ensureIsActiveVariant(variant);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExpressSubmitting, setIsExpressSubmitting] = useState(false);
  const [addToBasketSubmissionError, setAddToBasketSubmissionError] = useState<
    APIError | undefined
  >();

  const { basket, setBasket } = useBasket(
    runtimeConfig.basketApiBaseUrl,
    product.aliases.pimProductId,
  );

  const { quantity } = useQuantity();

  const showWishlistIconButtonVariant =
    !!flags.showWishlistIconButtonVariant.value;

  const [apiErrorState, setApiErrorState] = useState({
    addToBasketDisabled: false,
    expressCheckoutDisabled: false,
    buttonText: "Add to basket",
  });

  const buttonStates = useButtonStates({
    product,
    variant,
    isSelected,
    configProps,
    flags,
    isSubmitting,
    isExpressSubmitting,
    apiErrorState,
  });

  const { effectiveSelectedServices: services } = useSharedServices({
    product,
    flags,
  });

  const basketApiBaseUrl = runtimeConfig.basketApiBaseUrl;
  const basketCheckoutHost = runtimeConfig.basketCheckoutHost;

  useScrollToButton(product, a2bRef);

  useEffect(() => {
    setAddToBasketSubmissionError(undefined);
    setApiErrorState({
      addToBasketDisabled: false,
      expressCheckoutDisabled: false,
      buttonText: "Add to basket",
    });
  }, [variant.id]);

  useEffect(() => {
    const isShipFromStore = variant.availability?.shipFromStore === true;
    const expressCheckoutEnabled = configProps?.expressCheckoutEnabled === true;

    if (!isShipFromStore && expressCheckoutEnabled) {
      dispatchAnalyticsEvent(
        "global.impression",
        {
          type: "pdp",
          subType: "express checkout",
          additionalInfo: {},
          properties: {},
          products: [
            {
              brand: product.brand?.name,
              category: getDefaultCategory(product),
              sku: activeVariant?.aliases.skuId,
              price: activeVariant?.price?.value,
              productName: sanitiseTitle(variant?.title, getOwnBrand(product)),
              productId: product.aliases.pimProductId,
              colour: activeVariant?.differentiators?.colour,
              size: activeVariant?.differentiators?.size,
            },
          ],
        },
        {},
      );
    }
  }, [variant]);

  const focusAddToBasketButton = () => {
    addToBasketButtonRef.current?.focus();
  };

  const handleAddToBasketClick =
    (isExpressCheckout = false) =>
    async () => {
      if (!isSelected) {
        window.dispatchEvent(
          new CustomEvent(SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED),
        );
        return;
      }

      if (!activeVariant) {
        return;
      }

      await handleBasketSubmission(
        {
          basketApiBaseUrl,
          basketCheckoutHost,
          pimProductId: product.aliases.pimProductId,
          skuId: activeVariant.aliases.skuId,
          variantId: activeVariant.id,
          isExpressCheckout,
          isShipFromStore: activeVariant.availability.shipFromStore === true,
          price: activeVariant.price.value,
          title: product.title,
          services,
          quantity: isExpressCheckout ? 1 : quantity,
        },
        {
          setIsSubmitting,
          setIsExpressSubmitting,
          setAddToBasketSubmissionError,
          setBasket,
          setButtonState: setApiErrorState,
          focusAddToBasketButton,
        },
      );
    };

  if (!buttonStates.shouldRenderSection) {
    return null;
  }

  return (
    <div ref={a2bRef} className={cx("container")}>
      {addToBasketSubmissionError && (
        <ErrorMessage error={addToBasketSubmissionError} />
      )}
      <div
        className={cx("buttonsContainer", {
          wlExperience: showWishlistIconButtonVariant,
        })}
      >
        {buttonStates.expressCheckout.visible && (
          <ExpressCheckoutButton
            className={cx("stackable")}
            disabled={!buttonStates.expressCheckout.enabled}
            asPrimaryButton={!buttonStates.addToBasket.visible}
            isShipFromStore={variant.availability?.shipFromStore === true}
            handleAddToBasketClick={handleAddToBasketClick(true)}
            isSubmitting={isExpressSubmitting}
          />
        )}
        {buttonStates.addToBasket.visible && (
          <AddToBasketButton
            className={cx("stackable")}
            ref={addToBasketButtonRef}
            disabled={!buttonStates.addToBasket.enabled}
            isSubmitting={isSubmitting}
            handleAddToBasketClick={handleAddToBasketClick()}
            buttonText={buttonStates.addToBasket.text}
          />
        )}
        <Wishlist
          showWishlistIconButtonVariant={showWishlistIconButtonVariant}
          productId={product.aliases.pimProductId}
          skuId={variant.aliases.skuId}
          variantId={variant.id}
          isSelected={isSelected}
          isVariantAvailable={!variant.availability?.availableToOrder}
          addToBasketButtonState={buttonStates.addToBasket}
          expressButtonState={buttonStates.expressCheckout}
          accountBaseUrl={runtimeConfig.accountBaseUrl}
        />
      </div>
      {basket.length > 0 && (
        <div className={styles.confirmationBox}>
          <ProductBasketItems basket={basket} />
          <Recommendations
            {...props}
            configProps={{
              placementId:
                props.configProps?.recsPlacementId || "OSPStandardA2BClick",
              maxProductsPerPage: 2,
            }}
          />
        </div>
      )}
    </div>
  );
};
